import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { StateContext } from "../../../context/globalContext/context";
import { iGlobalContext } from "../../../context/globalContext/interface";
import { getActingPartner } from "../../../utils/actingPartner";
import { isValidJWT } from "../../../utils/authentication/isValidJWT";
import { describeJwt, embedAuth, embedAuthWarn } from "../../../utils/embedAuthLog";
import { isEmbedMode } from "../../../utils/embedMode";
import { isPartnerHost } from "../../../utils/embedHost";
import { PORTAL_MESSAGES, postToPortal } from "../../../utils/embedPortalBridge";
import {
  noteEmbedFrameToken,
  onExpiredEmbedToken,
  requestEmbedTokenRecover,
  retryExpiredEmbedToken,
  settleExpiredEmbedToken,
  TOKEN_RECOVER_THROTTLE_MS,
} from "../../../utils/embedTokenRecover";
import styles from "./embedSessionNotice.module.scss";

const GRACE_MS = 6000;

const AUTO_RECOVER_ATTEMPTS = 2;
const AUTO_RECOVER_INTERVAL_MS = 2500;
const RETRY_FEEDBACK_MS = 4000;
// A frame re-mounted with a stored, lapsed token drops its first calls before the
// portal has answered the boot sso.requested; give that answer time to land so the
// expired-session panel does not flash on every re-mount.
const TOKEN_PANEL_BOOT_GRACE_MS = 3000;

export default function EmbedSessionNotice() {
  const { userInfo, accessJWT }: iGlobalContext = useContext(StateContext);
  const [graceElapsed, setGraceElapsed] = useState(false);
  const [bootSettled, setBootSettled] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [autoAttempts, setAutoAttempts] = useState(0);
  const [expiredToken, setExpiredToken] = useState<string | null>(null);
  const [tokenFollowUps, setTokenFollowUps] = useState(0);
  const [autoExhausted, setAutoExhausted] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markRetrying = useCallback(() => {
    setRetrying(true);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => setRetrying(false), RETRY_FEEDBACK_MS);
  }, []);

  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!isEmbedMode()) return undefined;
    const timer = setTimeout(() => setGraceElapsed(true), GRACE_MS);
    const bootTimer = setTimeout(() => setBootSettled(true), TOKEN_PANEL_BOOT_GRACE_MS);
    return () => {
      clearTimeout(timer);
      clearTimeout(bootTimer);
    };
  }, []);

  useEffect(() => {
    if (isEmbedMode()) noteEmbedFrameToken(accessJWT);
  }, [accessJWT]);

  useEffect(() => {
    if (!isEmbedMode()) return undefined;
    return onExpiredEmbedToken(({ token, requested, exhausted }) => {
      setExpiredToken(token);
      setAutoExhausted(exhausted);
      if (requested) markRetrying();
    });
  }, [markRetrying]);

  const noIdentity = !userInfo?.loggedIn || !userInfo?.id;
  const missingPartner = isPartnerHost() && !getActingPartner()?.partnerCode;
  const unusable = isEmbedMode() && graceElapsed && (noIdentity || missingPartner);
  const broken = unusable && autoAttempts >= AUTO_RECOVER_ATTEMPTS;

  const superseded =
    !!expiredToken && (!accessJWT || (accessJWT !== expiredToken && isValidJWT(accessJWT)));
  const tokenPending = !!expiredToken && !superseded;
  const tokenExpired = tokenPending && bootSettled;

  useEffect(() => {
    if (!unusable || autoAttempts >= AUTO_RECOVER_ATTEMPTS) return undefined;
    const timer = setTimeout(() => {
      const delivered = postToPortal({ type: PORTAL_MESSAGES.sessionRecover });
      embedAuthWarn("session.recover.auto", { attempt: autoAttempts + 1, delivered });
      setAutoAttempts((n) => n + 1);
    }, autoAttempts === 0 ? 0 : AUTO_RECOVER_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [unusable, autoAttempts]);

  useEffect(() => {
    if (!superseded) return;
    embedAuth(accessJWT ? "session.token.recovered" : "session.token.cleared", {
      jwt: describeJwt(accessJWT),
    });
    settleExpiredEmbedToken();
    setExpiredToken(null);
    setAutoExhausted(false);
  }, [superseded, accessJWT]);

  useEffect(() => {
    if (!tokenPending || !expiredToken) return undefined;
    const timer = setTimeout(() => {
      const outcome = retryExpiredEmbedToken(expiredToken);
      if (outcome === "sent") markRetrying();
      if (outcome === "exhausted") setAutoExhausted(true);
      else setTokenFollowUps((n) => n + 1);
    }, TOKEN_RECOVER_THROTTLE_MS);
    return () => clearTimeout(timer);
  }, [tokenPending, expiredToken, tokenFollowUps, markRetrying]);

  useEffect(() => {
    if (!tokenExpired) return;
    embedAuthWarn("session.token.expired", { jwt: describeJwt(expiredToken) });
  }, [tokenExpired, expiredToken]);

  useEffect(() => {
    if (!broken) return;
    embedAuthWarn("session.unusable", {
      reason: noIdentity ? "no traveller identity" : "no acting partner on a partner-hosted frame",
      loggedIn: !!userInfo?.loggedIn,
      hasId: !!userInfo?.id,
      partnerCode: getActingPartner()?.partnerCode ?? null,
      partnerHost: isPartnerHost(),
    });
  }, [broken, noIdentity, userInfo?.loggedIn, userInfo?.id]);

  if (!broken && !tokenExpired) return null;

  const sessionExpired = tokenExpired && !broken;

  const retry = () => {
    markRetrying();
    if (sessionExpired) {
      requestEmbedTokenRecover();
      return;
    }
    const delivered = postToPortal({ type: PORTAL_MESSAGES.sessionRecover });
    embedAuthWarn("session.recover.requested", { delivered });
  };

  return (
    <div role="alert" className={styles.overlay}>
      <div className={styles.panel}>
        <div className={styles.title}>
          {sessionExpired ? "Your session expired" : "This session needs to reconnect"}
        </div>
        <div className={styles.body}>
          {sessionExpired
            ? autoExhausted
              ? "The console has not sent a fresh session. Press Retry, or Reload, then try that again."
              : "Reconnecting to the console for a fresh session. Once this clears, try that again."
            : missingPartner && !noIdentity
              ? "The console did not say which partner to act for, so prices cannot be quoted yet."
              : "The console has not finished handing this window its session."}
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={retry} disabled={retrying} className={styles.retryBtn}>
            {retrying ? "Reconnecting…" : "Retry"}
          </button>
          <button
            type="button"
            onClick={() => {
              const delivered = postToPortal({ type: PORTAL_MESSAGES.reloadHost });
              embedAuthWarn("session.reload.requested", { delivered, scope: delivered ? "host" : "frame" });
              if (!delivered) window.location.reload();
            }}
            className={styles.reloadBtn}
          >
            Reload
          </button>
        </div>
        <div className={styles.hint}>
          Still stuck? Sign out and back in to the console — that reissues the session this
          window uses.
        </div>
      </div>
    </div>
  );
}
